import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Twitter, Linkedin } from 'lucide-react';
import { useSite } from '../../../contexts/SiteContext';
import { proxyImg } from '../lib/imgProxy';

/**
 * Be The Train Footer
 * Clean, organized with program links and social
 */
const BTTFooter = () => {
  const { footerConfig, siteConfig } = useSite();
  
  // Logo URL from config or default
  const logoUrl = siteConfig?.theme?.logo || 'https://customer-assets.emergentagent.com/job_5fff2c07-dfff-4220-a200-8d4fd9f6d613/artifacts/i6xi2h68_BTTLogo.png';
  
  // Default footer columns
  const columns = footerConfig?.columns || [
    {
      title: 'Programs',
      links: [
        { label: 'Community', href: '/community' },
        { label: 'Creator Lab', href: '/creator-lab' },
        { label: 'Full Steam', href: '/full-steam' },
        { label: 'Engine Room', href: '/engine-room' },
      ]
    },
    {
      title: 'Resources',
      links: [
        { label: 'The Book', href: '/book' },
        { label: 'Store', href: '/store' },
        { label: 'Podcast', href: '/podcast' },
        { label: 'Philosophy', href: '/philosophy' },
      ]
    },
    {
      title: 'About',
      links: [
        { label: 'Selina Ringel', href: '/about' },
        { label: 'Creator Lab', href: '/creator-lab' },
        { label: 'Apply', href: '/apply' },
      ]
    }
  ];
  
  const socialLinks = (footerConfig?.social_links && Object.keys(footerConfig.social_links).length > 0)
    ? footerConfig.social_links
    : {
        instagram: 'https://instagram.com/selinaringel',
        youtube: 'https://www.youtube.com/@BeTheTrain',
      };
  const copyright = '© 2026 Be The Train. All rights reserved.';
  
  // Social icon mapping
  const socialIcons = {
    instagram: Instagram,
    youtube: Youtube,
    twitter: Twitter,
    linkedin: Linkedin,
  };
  
  return (
    <footer className="bg-zinc-950 border-t border-white/10">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={proxyImg(logoUrl, { w: 280 })} 
                alt="Be The Train" 
                loading="lazy"
                decoding="async"
                className="h-16"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-xs">
              A creator ecosystem for filmmakers who are done waiting — and ready to build.
            </p>

            {/* Contact email */}
            <a
              href="mailto:hello@bethetrain.com"
              className="inline-block text-white/65 text-sm hover:text-[#00A86B] transition-colors mb-6"
              data-testid="footer-contact-email"
            >
              hello@bethetrain.com
            </a>

            {/* Social links */}
            <div className="flex gap-4">
              {Object.entries(socialLinks).map(([platform, url]) => {
                const Icon = socialIcons[platform];
                if (!Icon || !url) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-[#00A86B] hover:border-[#00A86B]/50 transition-colors"
                    aria-label={platform}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>
          
          {/* Link columns */}
          {columns.map((column, idx) => (
            <div key={idx}>
              <h3 className="text-[#00A86B] text-xs uppercase tracking-widest font-bold mb-4">
                {column.title}
              </h3>
              <ul className="space-y-3">
                {column.links.map((link, linkIdx) => (
                  <li key={linkIdx}>
                    <Link
                      to={link.href}
                      className="text-white/50 text-sm hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-sm">
              {copyright}
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/30 text-sm hover:text-white/50 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-white/30 text-sm hover:text-white/50 transition-colors">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default BTTFooter;
